/**
 * Events API (Gluska-Forge-API docs/events-api.md): Forge's own LogSnag.
 * Read routes need Events access (the owner, or users.events_access_enabled);
 * the project/channel/token routes need admin on top of that. None of these
 * depend on the active license. apiClient methods already return the response body;
 * the `{data}` envelope is unwrapped here once.
 */
import { apiClient } from "@/lib/api"

export type PushState = "none" | "queued" | "sent" | "suppressed" | "failed"
export type InterruptionLevel = "passive" | "active" | "time-sensitive"
export type PrefLevel = "all" | "notify_only" | "none"
export type ChartKind = "line" | "bar" | "stacked-bar" | "pie"
export type SeriesBy = "none" | "type" | "channel"
export type SeriesInterval = "hour" | "day" | "week" | "month"

/** Where an event happened: exact from lat/lng tags, else its city or country geocoded by the API. */
export interface EventLocation {
  lat: number
  lng: number
  label: string | null
  precision: "exact" | "city" | "country"
}

export interface EventMapPoint {
  lat: number
  lng: number
  label: string | null
  precision: EventLocation["precision"]
  count: number
  last_at: string | null
  events: { id: string; event: string; icon: string | null; project: string | null; occurred_at: string | null }[]
}

export interface EventMap {
  days: number
  project: string | null
  points: EventMapPoint[]
  located: number
  unplaced: number
  truncated: boolean
}

export interface EventEntry {
  id: string
  project: string | null
  project_id: number
  channel: string | null
  channel_id: number
  event: string
  description: string | null
  parser: "text" | "markdown"
  icon: string | null
  notify: boolean
  user_id: string | null
  tags: Record<string, string | number | boolean>
  occurred_at: string
  received_at: string
  historical: boolean
  push: PushState
  /** Absent from responses before the map shipped, hence optional. */
  location?: EventLocation | null
}

export interface EventProject {
  id: number
  slug: string
  name: string
  icon: string | null
  description: string | null
  retention_days: number | null
  push_shows_event_name: boolean
  /** Push body carries the message (plain text). Older API responses omit it: treat as on. */
  push_shows_details?: boolean
  push_burst_per_minute: number
  muted_until: string | null
  archived_at: string | null
  created_at: string | null
}

export interface EventProjectSummary extends EventProject {
  channel_count: number
  event_count: number
  last_event_at: string | null
}

export interface EventChannel {
  id: number
  project_id: number
  slug: string
  name: string
  description: string | null
  interruption_level: InterruptionLevel
  muted_until: string | null
  retention_days: number | null
  keep_forever: boolean
  event_count: number
  last_event_at: string | null
}

export interface EventInsight {
  id: number
  title: string
  icon: string | null
  value: number | string | null
  updated_at: string | null
}

export interface EventProjectDetail extends EventProject {
  channels: EventChannel[]
  insights: EventInsight[]
}

export interface EventEntryDetail extends EventEntry {
  project_detail: EventProject | null
  channel_detail: EventChannel | null
  user: EventIdentity | null
}

export interface FeedPage {
  data: EventEntry[]
  next_cursor: string | null
  newest_cursor: string | null
}

export interface FeedParams {
  channel?: string
  q?: string
  user?: string
  tag?: string[]
  before?: string
  after?: string
  limit?: number
}

export interface EventIdentity {
  id: string
  properties: Record<string, string | number | boolean>
  first_seen_at: string | null
  last_seen_at: string | null
  event_count: number
}

export interface EventIdentityDetail extends EventIdentity {
  channels: { channel: string | null; count: number }[]
  groups: { id: string; properties: Record<string, unknown>; created_at: string | null }[]
}

export interface UsersPage {
  data: EventIdentity[]
  meta: { page: number; last_page: number; total: number }
}

export interface EventType {
  id: number
  name: string
  channel: string | null
  channel_id: number
  icon: string | null
  total: number
  first_seen_at: string | null
  last_seen_at: string | null
}

export interface SeriesParams {
  types?: number[]
  channel?: string
  by?: SeriesBy
  interval?: SeriesInterval
  from?: string
  to?: string
  tz?: string
}

export interface Series {
  interval: SeriesInterval
  tz: string
  from: string
  to: string
  by: SeriesBy
  total: number
  previous_total: number
  change_pct: number | null
  series: { key: string; label: string; total: number; points: { t: string; v: number }[] }[]
}

export interface ChartConfig {
  kind: ChartKind
  types: number[]
  channel: string | null
  by: SeriesBy
  interval: SeriesInterval
  range_days: number
}

export interface SavedChart {
  id: number
  project_id: number
  name: string
  config: ChartConfig
  position: number
  created_by: number | null
  updated_at: string | null
}

export interface EventToken {
  id: number
  project_id: number
  name: string
  token_prefix: string
  scopes: ("log" | "insight" | "identify")[]
  created_by: number | null
  last_used_at: string | null
  expires_at: string | null
  revoked_at: string | null
  created_at: string | null
}

export interface MintedToken {
  data: EventToken
  token: string
  base_url: string
}

export interface ChannelPref {
  channel_id: number
  channel: string
  level: PrefLevel | null
  muted_until: string | null
  interruption_level: InterruptionLevel
}

export interface ProjectPref {
  project_id: number
  project: string
  name: string
  icon: string | null
  level: PrefLevel
  muted_until: string | null
  channels: ChannelPref[]
}

export interface NotificationPrefs {
  default_level: PrefLevel
  projects: ProjectPref[]
}

export interface PrefChange {
  project_id: number
  channel_id: number | null
  level: PrefLevel | "inherit"
  muted_until?: string | null
}

export interface PushDevice {
  id: number
  environment: "sandbox" | "production"
  bundle_id: string | null
  app_version: string | null
  device_name: string | null
  token_suffix: string
  last_registered_at: string | null
  last_success_at: string | null
  disabled_at: string | null
  disabled_reason: string | null
}

export interface TestPushResult {
  device: PushDevice | number | string
  outcome: "ok" | "prune" | "retry" | "fail"
  apns_status: number | null
  reason: string | null
}

/** Base URL senders point their LogSnag client at. */
export const EVENTS_INGEST_BASE = "https://api.gluska.co/api/events/v1"

const p = (project: string) => `/events/projects/${encodeURIComponent(project)}`

/** Axios serialises arrays as `tag[]=a&tag[]=b`, which is what the API reads. */
function feedQuery(params: FeedParams) {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) continue
    out[k] = v
  }
  return out
}

export const eventsApi = {
  projects: (archived = false) =>
    apiClient.get<{ data: EventProjectSummary[] }>("/events/projects", { params: archived ? { archived: 1 } : {} }).then((r) => r.data),
  project: (project: string) => apiClient.get<{ data: EventProjectDetail }>(p(project)).then((r) => r.data),
  channels: (project: string) => apiClient.get<{ data: EventChannel[] }>(`${p(project)}/channels`).then((r) => r.data),

  feed: (project: string | null, params: FeedParams) =>
    apiClient.get<FeedPage>(project ? `${p(project)}/feed` : "/events/feed", { params: feedQuery(params) }),
  entry: (ulid: string) => apiClient.get<{ data: EventEntryDetail }>(`/events/entries/${encodeURIComponent(ulid)}`).then((r) => r.data),

  tags: (project: string) =>
    apiClient.get<{ data: { key: string; uses: number; distinct_values: number }[] }>(`${p(project)}/tags`).then((r) => r.data),
  tagValues: (project: string, key: string, q = "") =>
    apiClient
      .get<{ data: { value: string; uses: number }[] }>(`${p(project)}/tags/${encodeURIComponent(key)}/values`, { params: q ? { q } : {} })
      .then((r) => r.data),
  eventTypes: (project: string) => apiClient.get<{ data: EventType[] }>(`${p(project)}/event-types`).then((r) => r.data),
  insights: (project: string) => apiClient.get<{ data: EventInsight[] }>(`${p(project)}/insights`).then((r) => r.data),
  map: (project: string | null, days: number) =>
    apiClient.get<{ data: EventMap }>(project ? `${p(project)}/map` : "/events/map", { params: { days } }).then((r) => r.data),

  users: (project: string, params: { q?: string; page?: number; limit?: number }) =>
    apiClient.get<UsersPage>(`${p(project)}/users`, { params: feedQuery(params as FeedParams) }),
  user: (project: string, id: string) =>
    apiClient.get<{ data: EventIdentityDetail }>(`${p(project)}/users/${encodeURIComponent(id)}`).then((r) => r.data),
  userEvents: (project: string, id: string, params: FeedParams) =>
    apiClient.get<FeedPage>(`${p(project)}/users/${encodeURIComponent(id)}/events`, { params: feedQuery(params) }),

  series: (project: string, params: SeriesParams) =>
    apiClient
      .get<{ data: Series }>(`${p(project)}/series`, { params: feedQuery(params as unknown as FeedParams) })
      .then((r) => r.data),
  charts: (project: string) => apiClient.get<{ data: SavedChart[] }>(`${p(project)}/charts`).then((r) => r.data),
  createChart: (project: string, body: { name: string; config: ChartConfig; position?: number }) =>
    apiClient.post<{ data: SavedChart }>(`${p(project)}/charts`, body).then((r) => r.data),
  updateChart: (project: string, id: number, body: Partial<{ name: string; config: ChartConfig; position: number }>) =>
    apiClient.put<{ data: SavedChart }>(`${p(project)}/charts/${id}`, body).then((r) => r.data),
  deleteChart: (project: string, id: number) => apiClient.delete(`${p(project)}/charts/${id}`),

  prefs: () => apiClient.get<{ data: NotificationPrefs }>("/events/notification-prefs").then((r) => r.data),
  savePrefs: (prefs: PrefChange[]) =>
    apiClient.put<{ data: NotificationPrefs }>("/events/notification-prefs", { prefs }).then((r) => r.data),
  mute: (project: string, until: string | null, channel?: string) =>
    apiClient.post(`${p(project)}/mute`, channel ? { until, channel } : { until }),

  // Admin
  createProject: (body: { slug: string; name: string; icon?: string; description?: string; retention_days?: number | null }) =>
    apiClient.post<{ data: EventProject }>("/events/projects", body).then((r) => r.data),
  updateProject: (project: string, body: Partial<EventProject> & { archived?: boolean }) =>
    apiClient.put<{ data: EventProject }>(p(project), body).then((r) => r.data),
  archiveProject: (project: string) => apiClient.delete<{ data: EventProject }>(p(project)).then((r) => r.data),
  updateChannel: (
    project: string,
    channel: string,
    body: Partial<Pick<EventChannel, "name" | "description" | "interruption_level" | "retention_days" | "keep_forever" | "muted_until">>,
  ) => apiClient.put<{ data: EventChannel }>(`${p(project)}/channels/${encodeURIComponent(channel)}`, body).then((r) => r.data),
  tokens: (project: string) => apiClient.get<{ data: EventToken[] }>(`${p(project)}/tokens`).then((r) => r.data),
  createToken: (project: string, body: { name: string; scopes?: string[]; expires_at?: string | null }) =>
    apiClient.post<MintedToken>(`${p(project)}/tokens`, body),
  revokeToken: (project: string, id: number) =>
    apiClient.delete<{ data: EventToken }>(`${p(project)}/tokens/${id}`).then((r) => r.data),
  testEvent: (project: string) => apiClient.post<{ data: EventEntry }>(`${p(project)}/test-event`).then((r) => r.data),

  // Push devices (any signed-in user)
  devices: () => apiClient.get<{ data: PushDevice[] }>("/push-devices").then((r) => r.data),
  removeDevice: (id: number) => apiClient.delete(`/push-devices/${id}`),
  testPush: () => apiClient.post<{ data: TestPushResult[] }>("/push-devices/test").then((r) => r.data),
}
